import { useLocks } from '../../api/customerView.ts';
import { useRole } from '../../api/role.ts';
import { formatEnergy, formatPercent, formatPower, hasPermission } from '@ste/shared';
import { useLive } from '../../api/live.ts';
import { useCommands, useSite } from '../../api/queries.ts';
import { Card, EmptyState, PageHeader, SliderSetting, Spinner, Stat, Toggle } from '../../components/ui.tsx';
import { t } from '../../i18n/index.ts';
import { CommandError } from '../charging/CommandError.tsx';

export function BatteryPage() {
  const live = useLive();
  const site = useSite().data;
  const role = useRole();
  const commands = useCommands();
  const locked = useLocks().battery;
  if (!live) return <Spinner label={t('common.loading')} />;

  const battery = live.battery;
  if (!battery) {
    return (
      <div className="space-y-4">
        <PageHeader title={t('battery.title')} />
        <Card>
          <EmptyState>{t('battery.none')}</EmptyState>
        </Card>
      </div>
    );
  }

  // priority and buffer work with every battery, only discharge control needs a controllable one
  // (evcc core/site_api.go); owners, installers and admins may change them (Christian, 17.10.)
  const canControl = role !== undefined && hasPermission(role, 'site:control') && !locked;
  const canDischargeControl = canControl && site?.batteryControllable !== false;
  const settings = live.batterySettings ?? {};
  const state =
    Math.abs(battery.power) < 30
      ? t('flow.idle')
      : battery.power < 0
        ? t('flow.charging')
        : t('flow.discharging');

  return (
    <div className="space-y-4">
      <PageHeader title={t('battery.title')} />
      <Card>
        <div className="grid grid-cols-2 gap-6 sm:grid-cols-3">
          <Stat label={t('battery.soc')} value={formatPercent(battery.soc)} tone="accent" />
          <Stat label={t('battery.power')} value={formatPower(Math.abs(battery.power))} hint={state} />
          {battery.capacity !== undefined && (
            <Stat label="Kapazität" value={formatEnergy(battery.capacity)} />
          )}
        </div>
        <div
          className="mt-6 h-3 overflow-hidden rounded-full bg-surface-2"
          role="meter"
          aria-valuenow={battery.soc}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={t('battery.soc')}
        >
          <div
            className="h-full rounded-full bg-battery transition-all"
            style={{ width: `${Math.min(100, Math.max(0, battery.soc))}%` }}
          />
        </div>
      </Card>

      <Card title={t('nav.settings')}>
        <div className="space-y-8">
          {!canControl && (
            <p className="text-sm text-muted">{locked ? t('locks.installer') : t('common.noPermission')}</p>
          )}
          <SliderSetting
            label={t('battery.prioritySoc')}
            hint={t('battery.prioritySocHint')}
            value={settings.prioritySoc ?? 0}
            min={0}
            max={100}
            step={5}
            format={(v) => formatPercent(v)}
            disabled={!canControl}
            // evcc refuses a priority above the buffer: raise the buffer along with it
            onCommit={(prioritySoc) =>
              commands.setBattery.mutate(
                prioritySoc > (settings.bufferSoc ?? 100)
                  ? { prioritySoc, bufferSoc: prioritySoc }
                  : { prioritySoc },
              )
            }
          />
          <SliderSetting
            label={t('battery.bufferSoc')}
            hint={t('battery.bufferSocHint')}
            value={settings.bufferSoc ?? 100}
            min={0}
            max={100}
            step={5}
            format={(v) => formatPercent(v)}
            disabled={!canControl}
            onCommit={(bufferSoc) =>
              commands.setBattery.mutate(
                bufferSoc < (settings.prioritySoc ?? 0)
                  ? { bufferSoc, prioritySoc: bufferSoc }
                  : { bufferSoc },
              )
            }
          />
          <Toggle
            label={t('battery.dischargeControl')}
            hint={t('battery.dischargeControlHint')}
            checked={settings.dischargeControl === true}
            disabled={!canDischargeControl || commands.setBattery.isPending}
            onChange={(dischargeControl) => commands.setBattery.mutate({ dischargeControl })}
          />
          {canControl && !canDischargeControl && (
            <p className="text-sm text-muted">{t('battery.notControllable')}</p>
          )}
          {commands.setBattery.isError && <CommandError error={commands.setBattery.error} />}
        </div>
      </Card>
    </div>
  );
}
